import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';

import { textStyleClass } from '@sirius/typography';

import { NavigationData } from '../../_shared/data/navigation.data';
import { TrackingEvents } from '../../_shared/data/tracking-events.data';
import { LinkDirective } from '../../_shared/directives/link.directive';
import { ILanguagePicker } from '../../_shared/models/language-picker/language-picker.model';
import { ILink } from '../../_shared/models/link.model';
import {
    INavigationContext,
    IUtilityTab,
    IUtilityTabRegionalLink,
    UtilityTabName,
} from '../../_shared/models/navigation/navigation-hub.model';
import { IUtilityTabView } from '../../_shared/models/navigation/utility-bar-view.model';
import { INavigationLinkOverride } from '../../_shared/models/navigation/navigation-link-override';
import { GtmService } from '../../_shared/services/gtm.service';
import { LinkService } from '../../_shared/services/link.service';
import { SnowplowService } from '../../_shared/services/snowplow.service';
import { IconComponent } from '../../icon/icon.component';
import { LanguagePickerComponent } from '../../language-picker/language-picker.component';
import { UtilityBarService } from './utility-bar.service';

@Component({
    selector: 'sirius-utility-bar',
    templateUrl: './utility-bar.component.html',
    styleUrls: ['./utility-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LinkDirective, IconComponent, LanguagePickerComponent],
})
export class UtilityBarComponent {
    private readonly _gtmService = inject(GtmService);
    private readonly _snowplowService = inject(SnowplowService);
    private readonly _linkService = inject(LinkService);
    private readonly _utilBarService = inject(UtilityBarService);

    readonly globalLinks = input<ILink[]>([]);
    readonly languages = input<ILanguagePicker | undefined>(undefined);
    readonly activeTab = input<UtilityTabName>('main');
    readonly navigationLinkOverrides = input<INavigationLinkOverride[]>([]);
    readonly culture = input<string | undefined>(undefined);

    readonly clickEvent = output<void>();

    readonly showLanguageSelector = signal(false);

    readonly navigationContext = computed<INavigationContext>(() => ({ culture: this.culture() }));

    readonly navigationTabs = computed<IUtilityTab[]>(() =>
        this.resolveTabs(NavigationData.tabs, this.navigationLinkOverrides(), this.navigationContext())
    );

    protected readonly tabViews = computed<IUtilityTabView[]>(() => {
        const activeTab = this.activeTab();
        return this.navigationTabs().map((tab) => {
            const isActive = tab.name === activeTab;
            return {
                tab,
                isActive,
                linkClass: `sir-utility-bar-tab-link ${textStyleClass('system', 's', isActive ? 'bold' : 'default')}`,
            };
        });
    });

    constructor() {
        // Sibling-element bridge: utility-bar and navigation are mounted as
        // independent elements, so the mobile drawer (rendered inside
        // navigation) reads this snapshot from the shared service in its
        // ngOnInit instead of via input binding.
        effect(() => {
            this._utilBarService.globalLinks = this.globalLinks();
            this._utilBarService.languages = this.languages();
            this._utilBarService.activeTab = this.activeTab();
            this._utilBarService.navigationTabs = this.navigationTabs();
        });
    }

    onLanguageSelectorToggle(): void {
        this.showLanguageSelector.update((x) => !x);
    }

    onLanguageSelectorClose(): void {
        // sirius-modal2 restores focus to the trigger on close.
        this.showLanguageSelector.set(false);
    }

    onClick(link: ILink, e: Event): void {
        const anchor = e.currentTarget as HTMLElement | null;
        if (!anchor) {
            this.clickEvent.emit();
            return;
        }

        this.addTracking(link, anchor);
        this.clickEvent.emit();
    }

    //#region tracking
    private addTracking(link: ILink, anchor: HTMLElement): void {
        const data = this._linkService.getTrackingData(anchor);
        const cfg = {
            ...link?.tracking?.parameters,
            link_classes: data.linkClasses,
            link_id: data.linkId,
            link_text: data.linkText,
            link_url: data.linkUrl,
            outbound: data.outbound,
        };

        this._gtmService.track(TrackingEvents.gtm['topMenuClick']([cfg]));

        const cfg2 = {
            identifier: link.tracking?.parameters['identifier'],
            category: 'utility bar',
            element_classes: data.linkClasses.split(' '),
            target_url: `${this._linkService.normalizeHref(link.href) ?? ''}`,
            element_content: data.linkText,
            outbound: data.outbound !== 'FALSE',
            context: [],
        };

        this._snowplowService.track('linkClick', cfg2);
    }

    private resolveTabs(
        tabs: IUtilityTab[],
        overrides: INavigationLinkOverride[],
        context: INavigationContext
    ): IUtilityTab[] {
        return tabs.map((tab: IUtilityTab) => {
            const override = overrides.find((x: INavigationLinkOverride) => x.name === tab.name);

            return {
                ...tab,
                link: {
                    ...tab.link,
                    href: override?.href ?? this.resolveTabHref(tab, context),
                },
            };
        });
    }

    private resolveTabHref(tab: IUtilityTab, context: INavigationContext): string | undefined {
        const regionalHref = tab.regionalLinks?.find((x: IUtilityTabRegionalLink) =>
            this.isRegionalLinkMatch(x, context)
        )?.href;

        return regionalHref ?? tab.link.href;
    }

    private isRegionalLinkMatch(link: IUtilityTabRegionalLink, context: INavigationContext): boolean {
        const culture = context.culture?.toLowerCase();
        return culture ? (link.cultures?.some((x: string) => x.toLowerCase() === culture) ?? false) : false;
    }
    //#endregion
}
